import { GregorioClefInfo } from './GregorioClef';
import { GregorioLedgerSpecifity } from '../enums/GregorioLedgerSpecifity';
import { GregorioLiquescentia, TAIL_LIQUESCENTIA_MASK } from '../enums/GregorioLiquescentia';
import { GregorioShape } from '../enums/GregorioShape';
import { GregorioSign } from '../enums/GregorioSign';
import { GregorioEndOfLineInfo, GregorioExtraInfo } from './GregorioSyllable';
import { GregorioType } from '../enums/GregorioType';
import { GregorioVPosition } from '../enums/GregorioVPosition';
import { LOWEST_PITCH } from '../constants';
import { GregorioClefType } from '../enums/GregorioClefType';
import { GregorioSpace } from '../enums/GregorioSpace';
import { GregorioBar } from '../enums/GregorioBar';
import { GrehepisemaSize } from '../../gregorio-chant-svg-renderer/episemas/enums';
import { gregorio_add_h_episema } from '../score_parsing/gregorio_add_h_episema';
import { InterpreterContext } from '../parsers/compounded/CompoundedInterpreter';

export enum HEPISEMA {
  NONE = 0,
  AUTO = -1,
  FORCED = -2,
}

export class GregorioNoteElement {
  choral_sign: string | null = null;
  src_line!: number;
  src_column!: number;
  src_offset!: number;

  type!: GregorioType;

  signs: GregorioSign = GregorioSign.NO_SIGN;
  special_sign!: GregorioSign;

  high_ledger_line = false;
  high_ledger_specificity!: GregorioLedgerSpecifity;
  low_ledger_line = false;
  low_ledger_specificity!: GregorioLedgerSpecifity;
  is_lower_note = false;
  is_upper_note = false;
  mora_vposition!: GregorioVPosition;
  choral_sign_is_nabc = false;

  pitch!: number;

  v_episema_height!: number;
  h_episema_above: number = HEPISEMA.NONE;
  h_episema_below: number = HEPISEMA.NONE;
  h_episema_above_size!: GrehepisemaSize;
  h_episema_below_size!: GrehepisemaSize;
  h_episema_above_connect!: boolean;
  h_episema_below_connect!: boolean;
  gtex_offset_case!: string;

  private _shape: GregorioShape = GregorioShape.UNDETERMINED;

  public get shape() {
    return this._shape;
  }

  public set shape(shape: GregorioShape) {
    this._shape = shape;
  }

  liquescentia!: GregorioLiquescentia;
  private _is_cavum = false;

  get is_cavum() {
    return this._is_cavum;
  }

  public set_cavum(is_cavum: boolean) {
    this._is_cavum = is_cavum;
  }

  clef!: GregorioClefInfo;
  other!: GregorioExtraInfo;

  static buildNote(
    text: string,
    shape: GregorioShape,
    signs: GregorioSign,
    liquescentia: GregorioLiquescentia,
  ): GregorioNoteElement {
    const el = new GregorioNoteElement();
    const pitch = pitchLetterToHeight(text);
    // el.note = new GregorioNote(shape, liquescentia, pitch);

    el.pitch = pitch;
    el._shape = shape;
    el.liquescentia = liquescentia;

    el.type = GregorioType.NOTE;
    el.signs = signs;
    // el.texverb = 0;

    return el;
  }

  static buildClef(clef: GregorioClefType, line: number, flatted: boolean) {
    const el = new GregorioNoteElement();
    el.type = GregorioType.CLEF;
    el.clef = new GregorioClefInfo();
    el.clef.clef = clef;
    el.clef.line = line;
    el.clef.flatted = flatted;
    return el;
  }


  get needsLedgerLine() {
    return !(this.pitch > 4 && this.pitch < 14);
  }

  get ledgerLinePitch() {
    return this.pitch % 2 === 0 ? this.pitch : this.pitch + (this.pitch > 9 ? -1 : 1);
  }

  public addSign(sign: GregorioSign, text: string) {
    let vposition = GregorioVPosition.AUTO;

    switch (text[1]) {
      case '0':
        vposition = GregorioVPosition.BELOW;
        break;
      case '1':
        vposition = GregorioVPosition.ABOVE;
        break;
    }

    switch (sign) {
      case GregorioSign.PUNCTUM_MORA:
        switch (this.signs) {
          case GregorioSign.NO_SIGN:
            this.signs = GregorioSign.PUNCTUM_MORA;
            break;
          case GregorioSign.V_EPISEMA:
            this.signs = GregorioSign.V_EPISEMA_PUNCTUM_MORA;
            break;
          case GregorioSign.PUNCTUM_MORA:
            this.signs = GregorioSign.AUCTUM_DUPLEX;
            break;
          case GregorioSign.V_EPISEMA_PUNCTUM_MORA:
            this.signs = GregorioSign.V_EPISEMA_AUCTUM_DUPLEX;
            break;
          default:
            break;
        }

        this.mora_vposition = vposition;
        break;

      case GregorioSign.V_EPISEMA:
        switch (this.signs) {
          case GregorioSign.NO_SIGN:
            this.signs = GregorioSign.V_EPISEMA;
            break;
          case GregorioSign.PUNCTUM_MORA:
            this.signs = GregorioSign.V_EPISEMA_PUNCTUM_MORA;
            break;
          case GregorioSign.AUCTUM_DUPLEX:
            this.signs = GregorioSign.V_EPISEMA_AUCTUM_DUPLEX;
            break;
          default:
            break;
        }

        if (this.type === GregorioType.NOTE && vposition) {
          this.v_episema_height = this.pitch + vposition;
        }
        break;

      default:
        // Not reachable unless there's a programming error
        console.error('unexpected sign to add:', sign);
        break;
    }
  }


  public addTailLiquescentia(
    liq: GregorioLiquescentia,
    legacy_oriscus_orientation: boolean
  ): void {
    this.liquescentia = (this.liquescentia & ~TAIL_LIQUESCENTIA_MASK) | (liq & TAIL_LIQUESCENTIA_MASK);

    switch (this.shape) {
      case GregorioShape.STROPHA:
      case GregorioShape.DISTROPHA:
      case GregorioShape.TRISTROPHA:
        if (this.liquescentia & GregorioLiquescentia.AUCTUS_DESCENDENS) {
          this.liquescentia =
            (this.liquescentia & ~TAIL_LIQUESCENTIA_MASK) |
            GregorioLiquescentia.AUCTUS_ASCENDENS;
        }
        break;

      case GregorioShape.ORISCUS_UNDETERMINED:
      case GregorioShape.ORISCUS_ASCENDENS:
      case GregorioShape.ORISCUS_DESCENDENS:
      case GregorioShape.ORISCUS_DEMINUTUS:
        this.fix_oriscus_liquescentia(legacy_oriscus_orientation);
        break;

      case GregorioShape.ORISCUS_SCAPUS_UNDETERMINED:
      case GregorioShape.ORISCUS_SCAPUS_ASCENDENS:
      case GregorioShape.ORISCUS_SCAPUS_DESCENDENS:
        this.fix_oriscus_scapus_liquescentia(legacy_oriscus_orientation);
        break;

      default:
        break;
    }
  }

  public changeShape(shape: GregorioShape) {
    this._shape = shape;
    switch (shape) {
      case GregorioShape.STROPHA:
      case GregorioShape.DISTROPHA:
      case GregorioShape.TRISTROPHA:
        this.liquescentia &= GregorioLiquescentia.AUCTUS_ASCENDENS | GregorioLiquescentia.INITIO_DEBILIS;
        break;

      case GregorioShape.ORISCUS_UNDETERMINED:
        this.shape = GregorioShape.ORISCUS_DESCENDENS;
      // fall through
      case GregorioShape.ORISCUS_ASCENDENS:
      case GregorioShape.ORISCUS_DESCENDENS:
        this.fix_oriscus_liquescentia(false);
        break;

      case GregorioShape.ORISCUS_SCAPUS_UNDETERMINED:
        this.shape = GregorioShape.ORISCUS_SCAPUS_DESCENDENS;
      // fall through
      case GregorioShape.ORISCUS_SCAPUS_ASCENDENS:
      case GregorioShape.ORISCUS_SCAPUS_DESCENDENS:
        this.fix_oriscus_scapus_liquescentia(false);
        break;

      default:
        break;
    }
  }

  private fix_oriscus_scapus_liquescentia(
    legacy_oriscus_orientation: boolean
  ): void {
    if (legacy_oriscus_orientation) {
      switch (this.liquescentia) {
        case GregorioLiquescentia.AUCTUS_ASCENDENS:
          this.liquescentia =
            (this.liquescentia & ~TAIL_LIQUESCENTIA_MASK) |
            GregorioLiquescentia.AUCTUS_DESCENDENS;
        /* fall through */
        case GregorioLiquescentia.AUCTUS_DESCENDENS:
          this._shape = GregorioShape.ORISCUS_SCAPUS_DESCENDENS;
          break;
        default:
          this._shape = GregorioShape.ORISCUS_SCAPUS_ASCENDENS;
          break;
      }
    } else {
      this.liquescentia &= ~TAIL_LIQUESCENTIA_MASK;
    }
  }

  private fix_oriscus_liquescentia(
    legacy_oriscus_orientation: boolean
  ): void {
    if (legacy_oriscus_orientation) {
      switch (this.liquescentia) {
        case GregorioLiquescentia.AUCTUS_ASCENDENS:
          this.liquescentia =
            (this.liquescentia & ~TAIL_LIQUESCENTIA_MASK) |
            GregorioLiquescentia.AUCTUS_DESCENDENS;
        /* fall through */
        case GregorioLiquescentia.AUCTUS_DESCENDENS:
          this._shape = GregorioShape.ORISCUS_DESCENDENS;
          break;
        case GregorioLiquescentia.DEMINUTUS:
          this._shape = GregorioShape.ORISCUS_DEMINUTUS;
          break;
        default:
          this._shape = GregorioShape.ORISCUS_ASCENDENS;
          break;
      }
    } else {
      this.liquescentia &= ~TAIL_LIQUESCENTIA_MASK | GregorioLiquescentia.DEMINUTUS;
      if (this.liquescentia & GregorioLiquescentia.DEMINUTUS) {
        this._shape = GregorioShape.ORISCUS_DEMINUTUS;
      }
    }
  }

  public static buildEndOfLine(eol_ragged: boolean, eol_forces_custos: boolean, eol_forces_custos_on: boolean) {
    const el = new GregorioNoteElement();
    el.type = GregorioType.END_OF_LINE;
    el.other = new GregorioExtraInfo();
    el.other.eol = new GregorioEndOfLineInfo();
    el.other.eol.eol_ragged = eol_ragged;
    el.other.eol.eol_forces_custos = eol_forces_custos;
    el.other.eol.eol_forces_custos_on = eol_forces_custos_on;
    return el;
  }

  public static buildSpace(space: GregorioSpace, factor: string | undefined = undefined) {
    const el = new GregorioNoteElement();
    el.type = GregorioType.SPACE;
    el.other = new GregorioExtraInfo();
    el.other.space = space;
    el.other.ad_hoc_space_factor = factor;
    return el;
  }

  public static buildCustos() {
    const el = new GregorioNoteElement();
    el.type = GregorioType.CUSTOS;
    return el;
  }

  public static buildManualCustos(text: string) {
    const el = new GregorioNoteElement();
    el.type = GregorioType.MANUAL_CUSTOS;
    el.pitch = pitchLetterToHeight(text);
    return el;
  }

  static buildSeparatinBar(bar: GregorioBar) {
    const el = new GregorioNoteElement();
    el.type = GregorioType.BAR;
    el.other = new GregorioExtraInfo();
    el.other.bar = bar;
    return el;
  }

  static buildDominicanBar(barStr: string) {
    const barNumber = parseInt(barStr, 10);
    let determinedBar: GregorioBar = GregorioBar.DIVISIO_MINOR;

    switch (barNumber) {
      case 1:
        determinedBar = GregorioBar.DIVISIO_MINOR_D1;
        break;
      case 2:
        determinedBar = GregorioBar.DIVISIO_MINOR_D2;
        break;
      case 3:
        determinedBar = GregorioBar.DIVISIO_MINOR_D3;
        break;
      case 4:
        determinedBar = GregorioBar.DIVISIO_MINOR_D4;
        break;
      case 5:
        determinedBar = GregorioBar.DIVISIO_MINOR_D5;
        break;
      case 6:
        determinedBar = GregorioBar.DIVISIO_MINOR_D6;
        break;
      case 7:
        determinedBar = GregorioBar.DIVISIO_MINOR_D7;
        break;
      case 8:
        determinedBar = GregorioBar.DIVISIO_MINOR_D8;
        break;
    }

    return this.buildSeparatinBar(determinedBar);
  }

  addHorizontalEpisemaForBar() {
    this.special_sign = GregorioSign.BAR_H_EPISEMA;
  }


  // addHorizontalEpisema(position?: GregorioVPosition, pitch?: number) {
  //   const usedPitch = pitch || this.pitch;

  // switch (position) {
  //   case GregorioVPosition.BELOW:
  //     this.h_episema_position = usedPitch % 2 === 0 ? usedPitch - 1 : usedPitch - 2;
  //     this.h_episema_mode = GregorioVPosition.BELOW;
  //     break;
  //   case GregorioVPosition.ABOVE:
  //     this.h_episema_position = usedPitch % 2 === 0 ? usedPitch + 1 : usedPitch + 2;
  //     this.h_episema_mode = GregorioVPosition.ABOVE;
  //     break;
  //   default:
  //     this.h_episema_mode = GregorioVPosition.AUTO;
  //     break;
  // }
  // }
}

function pitchLetterToHeight(pitch: string) {
  let result: number = pitch.toLowerCase().charCodeAt(0) - 97 + LOWEST_PITCH; // 97 is 'a'
  if (pitch === 'p') {
    --result;
  }

  // todo: whai is it?
  // if (left_bracket_texverb) {
  //     if (result < bracket_low_pitch) {
  //         bracket_low_pitch = result;
  //     }
  //     if (result > bracket_high_pitch) {
  //         bracket_high_pitch = result;
  //     }
  // }

  return result;
}

export function addHorizontalEpisemaFromText(notes: GregorioNoteElement[], note_i: number,
  text: string, interpreter_context: InterpreterContext) {
  let size = GrehepisemaSize.H_NORMAL;
  let vposition = GregorioVPosition.AUTO;
  let disable_bridge = false;

  /* first character is the underscore */
  for (const current of text) {
    switch (current) {
      case '0':
        vposition = GregorioVPosition.BELOW;
        break;
      case '1':
        vposition = GregorioVPosition.ABOVE;
        break;
      case '2':
        disable_bridge = true;
        break;
      case '3':
        size = GrehepisemaSize.H_SMALL_LEFT;
        break;
      case '4':
        size = GrehepisemaSize.H_SMALL_CENTRE;
        break;
      case '5':
        size = GrehepisemaSize.H_SMALL_RIGHT;
        break;
      default:
        /* not reachable unless there's a programming error */
        /* LCOV_EXCL_START */
        // throw new Error('unrecognized horizontal episema modifier: ' + current);
        return;
        // gregorio_fail2(gabc_notes_determination,
        //         "unrecognized horizontal episema modifier: %c", current);
        break;
      /* LCOV_EXCL_STOP */
    }
  }

  gregorio_add_h_episema(notes, note_i, size, vposition, disable_bridge,
    interpreter_context);
}
